import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, Permission } from '@zuri/shared';
import { api, ApiError, onApiError, setExpectedTenant } from './api';

interface SessionValue {
  me: MeResponse | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  refresh: () => Promise<void>;
  switchCompany: (tenantId: string) => Promise<MeResponse>;
  logout: () => Promise<void>;
  /** Aplica uma nova sessão (login, cadastro, troca) descartando dados em cache de outro contexto. */
  applySession: (next: MeResponse | null) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<MeResponse>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = query.data ?? null;
  setExpectedTenant(me?.activeCompany?.id ?? null);

  useEffect(
    () =>
      onApiError((e) => {
        if (e.code === 'TENANT_MISMATCH') window.location.reload();
        if (e.status === 401 && e.code === 'UNAUTHENTICATED') {
          // Sessão encerrada no servidor enquanto o usuário estava conectado: avisa no login.
          if (qc.getQueryData(['me'])) {
            try {
              sessionStorage.setItem('zuri-session-expired', '1');
            } catch {
              /* armazenamento indisponível */
            }
          }
          qc.setQueryData(['me'], null);
        }
      }),
    [qc],
  );

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  // Isolamento também no cliente: nenhum dado em cache de outra empresa/sessão sobrevive à troca.
  // A consulta 'me' é mantida (e atualizada) para que o provedor observe a nova sessão.
  const applySession = useCallback(
    (next: MeResponse | null) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      setExpectedTenant(next?.activeCompany?.id ?? null);
      qc.setQueryData(['me'], next);
    },
    [qc],
  );

  const switchCompany = useCallback(
    async (tenantId: string) => {
      const next = await api<MeResponse>('/auth/active-company', { body: { tenantId } });
      applySession(next);
      return next;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    await api('/auth/logout', { body: {} }).catch(() => undefined);
    applySession(null);
  }, [applySession]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading: query.isLoading,
      can: (p) => !!me?.permissions.includes(p),
      refresh,
      switchCompany,
      logout,
      applySession,
    }),
    [me, query.isLoading, refresh, switchCompany, logout, applySession],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession fora do SessionProvider');
  return ctx;
}

/** Empresa ativa (garantida nas rotas da área da empresa). */
export function useCompany() {
  const { me } = useSession();
  if (!me?.activeCompany) throw new Error('Sem empresa ativa');
  return me.activeCompany;
}

export function useCanWrite(): boolean {
  const { me } = useSession();
  return me?.access?.level === 'full';
}
